import type { KundeCreateBody } from "./types";
import { kundenName, namenTeilen, type Anrede } from "./anrede";

/**
 * Die Werte, die ein neuer Kunde braucht — geteilt zwischen der Kundenseite
 * und dem Anlege-Dialog im Belegeditor (Benjamin, 02.09.2026: „alle
 * Kundendaten, die auch beim Erfassen abgefragt werden"). Ein zweites,
 * abgespecktes Formular hätte Kunden erzeugt, denen später Felder fehlen.
 */

export type Kundentyp = "privat" | "firma";
export type Kommunikationskanal = "" | "telefon" | "mail" | "whatsapp" | "telegram";
/**
 * Ein Eintrag in `kunde.kontakte_json` (seit 24.09.2026 mit `art` und `anrede`).
 *
 * - `person`  — Ansprechpartner einer Firma, mit eigener Anrede. Die Firma
 *               selbst hat keine: angeschrieben wird die Person, an deren
 *               Adresse die Mail geht (lib/anrede.ts `anredeFuer`).
 * - `kontakt` — eine weitere Telefonnummer oder Mailadresse des Kunden selbst
 *               (Privat wie Firma); `role` ist dann die Bezeichnung („mobil",
 *               „Büro", „Zweitadresse").
 *
 * Eine Liste für beides, weil Mail-Zuordnung (connect_gmail), Versandvorschläge
 * und „neue Adressen" schon immer alle Adressen aus dieser Spalte lesen.
 */
export type KontaktArt = "person" | "kontakt";
export type PersonAnrede = "" | "frau" | "herr" | "du";
export const PERSON_ANREDEN: readonly PersonAnrede[] = ["", "frau", "herr", "du"];
export type KontaktZeile = {
  art: KontaktArt;
  /** Bei Personen aus Vor- und Nachname gebaut (`kontakteJson`); bei Kontaktwegen meist leer. */
  name: string;
  vorname: string;
  nachname: string;
  role: string;
  email: string;
  phone: string;
  anrede: PersonAnrede;
};

export type KundeWerte = {
  kundentyp: Kundentyp;
  name: string;
  email: string;
  phone: string;
  kommunikationskanal: Kommunikationskanal;
  website: string;
  strasse: string;
  plz: string;
  ort: string;
  rechnung_strasse: string;
  rechnung_plz: string;
  rechnung_ort: string;
  notes: string;
  anrede: Anrede;
  /** Nur Privatkunden (Migration 170); `name` wird daraus gebaut. */
  vorname: string;
  nachname: string;
  kontakte: KontaktZeile[];
  /** Monat "1".."12" → Beschreibung; nur angehakte Monate. */
  pflege_monate: Record<string, string>;
};

export const leereKundeWerte = (): KundeWerte => ({
  kundentyp: "privat", name: "", email: "", phone: "", kommunikationskanal: "",
  website: "", strasse: "", plz: "", ort: "",
  rechnung_strasse: "", rechnung_plz: "", rechnung_ort: "",
  notes: "", anrede: "", vorname: "", nachname: "", kontakte: [], pflege_monate: {},
});

export const leererKontakt = (): KontaktZeile => ({
  art: "person", name: "", vorname: "", nachname: "", role: "", email: "", phone: "", anrede: "",
});
export const leererKontaktweg = (): KontaktZeile => ({
  art: "kontakt", name: "", vorname: "", nachname: "", role: "", email: "", phone: "", anrede: "",
});

/**
 * Was gespeichert wird — Zeilen ohne jeden Inhalt fallen weg. Bei Personen wird
 * `name` aus Vor- und Nachname gebaut: Mail-Zuordnung und Vorschläge lesen ihn.
 */
export function kontakteJson(liste: KontaktZeile[]): string {
  return JSON.stringify(liste
    .map((k) => {
      const vorname = k.vorname.trim();
      const nachname = k.nachname.trim();
      const name = k.art === "person" && (vorname || nachname) ? [vorname, nachname].filter(Boolean).join(" ") : k.name.trim();
      return { ...k, name, vorname, nachname, email: k.email.trim(), phone: k.phone.trim(), role: k.role.trim() };
    })
    .filter((k) => k.name + k.email + k.phone !== ""));
}

type NamensForm = { kundentyp: "privat" | "firma"; name: string; anrede: Anrede; vorname: string; nachname: string };

/** Hat das Formular einen Namen? Privat: Vor- oder Nachname, Firma: der Firmenname. */
export function hatName(f: NamensForm): boolean {
  return f.kundentyp === "privat" ? !!(f.vorname.trim() || f.nachname.trim()) : !!f.name.trim();
}

/**
 * Privat ↔ Firma umschalten, ohne dass der Name verloren geht: aus Vor- und
 * Nachname wird der Firmenname vorbelegt, aus dem Firmennamen Vor- und Nachname.
 */
export function kundentypWechseln<T extends NamensForm>(f: T, neu: "privat" | "firma"): T {
  if (f.kundentyp === neu) return f;
  if (neu === "firma") return { ...f, kundentyp: neu, name: f.name.trim() || kundenName(f.anrede, f.vorname, f.nachname) };
  return { ...f, kundentyp: neu, ...(f.vorname.trim() || f.nachname.trim() ? {} : namenTeilen(f.name)) };
}

/**
 * Name, Anrede, Vor- und Nachname für den Body. Privat: `name` entsteht aus
 * Vor- und Nachname. Firma: der Firmenname, keine Anrede, keine Personenfelder —
 * die tragen dort die Ansprechpartner.
 */
export function nameFelder(f: { kundentyp: string; name: string; anrede: Anrede; vorname: string; nachname: string }) {
  if (f.kundentyp !== "privat") return { name: f.name.trim(), anrede: "" as Anrede, vorname: "", nachname: "" };
  return { name: kundenName(f.anrede, f.vorname, f.nachname), anrede: f.anrede, vorname: f.vorname.trim(), nachname: f.nachname.trim() };
}

/**
 * Die Kontaktliste eines Kunden aus `kontakte_json` lesen.
 *
 * Die Spalte ist Text, kein jsonb — jede Seite, die Ansprechpartner braucht,
 * muss sie selbst auspacken. Deshalb steht das Auspacken hier neben dem
 * Einpacken (`kundeCreateBody`) und nicht ein zweites Mal in jeder Seite.
 * Unlesbares gilt als „keine Kontakte": ein kaputtes Feld darf die Seite nicht
 * mitreißen.
 */
export function parseKontakte(raw: string): KontaktZeile[] {
  try {
    const arr = JSON.parse(raw || "[]");
    if (!Array.isArray(arr)) return [];
    return arr.map((x) => {
      const role = (x?.role ?? "").toString();
      // Altbestand ohne `art`: Zweitadressen (POST …/neue-adresse) sind
      // Kontaktwege, alles andere waren schon immer Ansprechpartner.
      const art: KontaktArt = x?.art === "kontakt" || x?.art === "person"
        ? x.art
        : role === "Zweitadresse" ? "kontakt" : "person";
      const anrede = (x?.anrede ?? "").toString() as PersonAnrede;
      const name = (x?.name ?? "").toString();
      // Ältere Einträge haben nur `name` — dann aufteilen (letztes Wort = Nachname).
      const geteilt = x?.vorname || x?.nachname
        ? { vorname: (x?.vorname ?? "").toString(), nachname: (x?.nachname ?? "").toString() }
        : art === "person" ? namenTeilen(name) : { vorname: "", nachname: "" };
      return {
        art,
        name,
        ...geteilt,
        role,
        email: (x?.email ?? "").toString(),
        phone: (x?.phone ?? "").toString(),
        anrede: PERSON_ANREDEN.includes(anrede) ? anrede : "",
      };
    });
  } catch {
    return [];
  }
}

/** Wie DB/Desktop: zwei Zeilen „Straße" + „PLZ Ort". */
export function denormAddr(strasse: string, plz: string, ort: string): string {
  const s = (strasse || "").trim();
  const rest = `${(plz || "").trim()} ${(ort || "").trim()}`.trim();
  if (s && rest) return `${s}\n${rest}`;
  return s || rest;
}

/** Der Body für POST /kunde — derselbe wie auf der Kundenseite. */
export function kundeBodyAusWerten(form: KundeWerte): KundeCreateBody {
  const pm: Record<string, string> = {};
  for (const [k, v] of Object.entries(form.pflege_monate)) pm[k] = v || "";
  return {
    ...nameFelder(form),
    kundentyp: form.kundentyp,
    // Klartext-Label wie im Desktop-CRM — sonst bleibt kunde.typ leer und
    // Desktop-Suche sowie Registry-Export laufen für Kontor-Kunden ins Leere.
    typ: form.kundentyp === "privat" ? "Privat" : "Firma",
    email: form.email,
    phone: form.phone,
    kommunikationskanal: form.kommunikationskanal,
    website: form.website,
    strasse: form.strasse,
    plz: form.plz,
    ort: form.ort,
    lieferadresse: denormAddr(form.strasse, form.plz, form.ort),
    rechnung_strasse: form.rechnung_strasse,
    rechnung_plz: form.rechnung_plz,
    rechnung_ort: form.rechnung_ort,
    rechnungsadresse: denormAddr(form.rechnung_strasse, form.rechnung_plz, form.rechnung_ort),
    notes: form.notes,
    // Alles speichern, auch Ansprechpartner eines Privatkunden: wer den Typ
    // umschaltet, soll nichts verlieren. Angezeigt werden sie nur bei Firmen.
    kontakte_json: kontakteJson(form.kontakte),
    pflege_monate_json: JSON.stringify(pm),
    regelmaessige_pflege: Object.keys(pm).length > 0,
  };
}

/* ── Stammdatenqualität beim Anlegen (Benjamin, 13.10.2026: „Die Stammdaten müssen
   konsistent und sauber in der Kundendatenbank landen") ─────────────────────── */

const ohneVorsatz = (s: string) =>
  (s || "").toLowerCase().replace(/\b(familie|fam\.?|herr|frau|eheleute)\b/g, " ").replace(/[^a-zäöüß0-9]+/g, " ").trim();
const ziffern = (s: string) => (s || "").replace(/\D/g, "").replace(/^(0049|49)/, "0");

/**
 * Kunden, die es vermutlich schon gibt: gleiche Mailadresse, gleiche Rufnummer
 * (die letzten neun Ziffern) oder gleicher Name. Ein Treffer ist ein Hinweis, kein
 * Verbot — zwei verschiedene Herren Müller gibt es.
 */
export function aehnlicheKunden<K extends { id: string; name: string; email?: string; phone?: string }>(
  form: Pick<KundeWerte, "kundentyp" | "name" | "anrede" | "vorname" | "nachname" | "email" | "phone">,
  kunden: K[],
): K[] {
  const name = ohneVorsatz(nameFelder(form).name);
  const mail = form.email.trim().toLowerCase();
  const tel = ziffern(form.phone);
  return kunden.filter((k) => {
    if (mail && (k.email || "").trim().toLowerCase() === mail) return true;
    const kt = ziffern(k.phone || "");
    if (tel.length >= 8 && kt.length >= 8 && kt.slice(-9) === tel.slice(-9)) return true;
    return name.length >= 4 && ohneVorsatz(k.name) === name;
  }).slice(0, 3);
}

/**
 * Was an der Anschrift fehlt — leer, wenn sie vollständig ist. `leer` heißt: gar
 * keine Anschrift (darf nach Rückfrage angelegt werden), sonst ist sie unvollständig
 * oder falsch und wird nicht gespeichert.
 */
export function adressPruefung(strasse: string, plz: string, ort: string): { fehler: string; leer: boolean; hinweis: string } {
  const s = strasse.trim(), p = plz.trim(), o = ort.trim();
  if (!s && !p && !o) return { fehler: "", leer: true, hinweis: "" };
  if (p && !/^\d{5}$/.test(p)) return { fehler: "Die PLZ hat fünf Ziffern.", leer: false, hinweis: "" };
  if (!s) return { fehler: "Straße und Hausnummer fehlen.", leer: false, hinweis: "" };
  if (!p || !o) return { fehler: "PLZ und Ort fehlen — am einfachsten einen Vorschlag aus der Adresssuche wählen.", leer: false, hinweis: "" };
  return { fehler: "", leer: false, hinweis: /\d/.test(s) ? "" : "Keine Hausnummer in der Straße." };
}
